/**
 * Beaut by Sterling — Diwali interactions
 *
 *  1. Cracker burst (anaar fountains, chakris, rockets and phuljhadi glitter) around every
 *     "Add to cart" / "Buy now" button. Purely visual: it never cancels or delays the click,
 *     so the theme cart and GoKwik checkout keep working exactly as before.
 *  2. <diwali-sky>        ambient fireworks behind the festive hero section
 *  3. <diwali-countdown>  countdown tiles to Diwali
 *  4. <diwali-coupon>     tap-to-copy festive coupon code
 *
 * Settings are read from window.DiwaliTheme (layout/theme.liquid) and map to
 * Theme settings › Diwali festive theme.
 */
(() => {
  'use strict';

  const config = Object.assign({ enabled: true, crackers: true, ambient: true }, window.DiwaliTheme);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const rand = (min, max) => min + Math.random() * (max - min);
  const pick = (list) => list[(Math.random() * list.length) | 0];
  const toRgbaPrefix = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, `;
  };

  // Saturated colours stay visible on the ivory page; bright ones glow on the night-sky hero.
  const LIGHT_PALETTES = [
    ['#ff8f1f', '#ff6a00', '#ffb627'],
    ['#c8102e', '#e11d48', '#ff4d6d'],
    ['#f72585', '#d6336c', '#ff70a6'],
    ['#e0a83a', '#c48a26', '#f2c14e'],
    ['#0f9d8a', '#12b886', '#2dd4a7'],
    ['#7b2cbf', '#9b5de5', '#c77dff'],
  ];
  const DARK_PALETTES = [
    ['#fff3cf', '#ffd166', '#f2c14e'],
    ['#ffb627', '#ff8f1f', '#ffe08a'],
    ['#ff4d8d', '#ffc2d9', '#fff0f6'],
    ['#7cffb2', '#2dd4a7', '#e6fff3'],
    ['#c4a7ff', '#9b5de5', '#f1e9ff'],
  ];
  const FOUNTAIN_LIGHT = ['#ff8f1f', '#ffb627', '#e0a83a', '#ff6a00'];
  const FOUNTAIN_DARK = ['#fff3cf', '#ffd166', '#ffb627'];

  /* ------------------------------------------------------------------------
     Particle engine shared by the click bursts and the hero sky
     ------------------------------------------------------------------------ */
  class FireworksEngine {
    constructor(canvas, { fullscreen = false, dark = false, maxParticles = 900 } = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.fullscreen = fullscreen;
      this.dark = dark;
      this.maxParticles = maxParticles;
      this.particles = [];
      this.emitters = [];
      this.rockets = [];
      this.flashes = [];
      this.raf = 0;
      this.last = 0;
      this.tick = this.tick.bind(this);
      this.resize = this.resize.bind(this);
      this.resize();
      if (fullscreen) window.addEventListener('resize', this.resize, { passive: true });
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      this.width = this.fullscreen ? window.innerWidth : rect.width;
      this.height = this.fullscreen ? window.innerHeight : rect.height;
      this.canvas.width = Math.max(1, Math.round(this.width * dpr));
      this.canvas.height = Math.max(1, Math.round(this.height * dpr));
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    get busy() {
      return this.particles.length + this.emitters.length + this.rockets.length + this.flashes.length > 0;
    }

    get palettes() {
      return this.dark ? DARK_PALETTES : LIGHT_PALETTES;
    }

    spark(x, y, angle, speed, opts = {}) {
      if (this.particles.length >= this.maxParticles) return;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        ttl: opts.ttl ?? rand(0.7, 1.3),
        color: opts.color ?? '#ffb627',
        size: opts.size ?? rand(1.4, 2.6),
        gravity: opts.gravity ?? 260,
        drag: opts.drag ?? 1.6,
        flicker: !!opts.flicker,
        crackle: !!opts.crackle,
      });
    }

    flash(x, y, radius, color = 'rgba(255, 182, 39, ') {
      this.flashes.push({ x, y, radius, color, life: 0, ttl: 0.45 });
    }

    /** Peony burst: a sphere of sparks with a soft flash at its heart. */
    burst(x, y, { count = 60, power = 240, palette = pick(this.palettes), crackle = false } = {}) {
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + rand(-0.09, 0.09);
        const speed = power * Math.pow(Math.random(), 0.4);
        this.spark(x, y, angle, speed, {
          color: pick(palette),
          ttl: rand(0.75, 1.35),
          size: this.dark ? rand(1.5, 2.4) : rand(2.2, 3.6),
          gravity: 150,
          drag: 1.9,
          flicker: Math.random() < 0.35,
          crackle,
        });
      }
      this.flash(x, y, power * (this.dark ? 0.32 : 0.42), this.dark ? 'rgba(255, 214, 102, ' : toRgbaPrefix(palette[0]));
    }

    /** Rocket that streaks from (x, y) to (tx, ty) after `delay` seconds, then bursts. */
    rocket(x, y, tx, ty, opts = {}) {
      this.rockets.push({
        x, y, sx: x, sy: y, tx, ty,
        wait: opts.delay ?? 0,
        t: 0,
        dur: opts.dur ?? rand(0.32, 0.48),
        palette: opts.palette ?? pick(this.palettes),
        power: opts.power ?? rand(170, 250),
        count: opts.count ?? 56,
        crackle: opts.crackle ?? Math.random() < 0.3,
      });
    }

    /** Anaar (flower pot): an upward fountain of sparks. */
    fountain(x, y, { duration = 0.7, angle = -Math.PI / 2, spread = 0.45, rate = 150, delay = 0 } = {}) {
      this.emitters.push({ type: 'fountain', x, y, angle, spread, rate, wait: delay, life: 0, ttl: duration, acc: 0 });
    }

    /** Chakri (ground spinner): sparks thrown off a spinning wheel. */
    chakri(x, y, { duration = 0.9, dir = 1, rate = 130, delay = 0 } = {}) {
      this.emitters.push({ type: 'chakri', x, y, dir, rate, wait: delay, life: 0, ttl: duration, acc: 0, theta: rand(0, Math.PI * 2) });
    }

    start() {
      if (this.raf) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    stop() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    tick(now) {
      const dt = Math.min((now - this.last) / 1000, 1 / 30);
      this.last = now;
      this.update(dt);
      this.draw();

      if (this.busy) {
        this.raf = requestAnimationFrame(this.tick);
      } else {
        this.raf = 0;
        this.ctx.clearRect(0, 0, this.width, this.height);
      }
    }

    update(dt) {
      const fountainColors = this.dark ? FOUNTAIN_DARK : FOUNTAIN_LIGHT;

      for (let i = this.emitters.length - 1; i >= 0; i--) {
        const e = this.emitters[i];
        if (e.wait > 0) { e.wait -= dt; continue; }
        e.life += dt;
        e.acc += e.rate * dt;

        while (e.acc >= 1) {
          e.acc -= 1;
          if (e.type === 'fountain') {
            this.spark(e.x + rand(-3, 3), e.y, e.angle + rand(-e.spread, e.spread), rand(190, 360), {
              color: pick(fountainColors), ttl: rand(0.4, 0.85), size: this.dark ? rand(1.2, 2.2) : rand(1.8, 3), gravity: 540, drag: 0.8, flicker: true,
            });
          } else {
            e.theta += 0.55 * e.dir;
            const tangent = e.theta + (Math.PI / 2) * e.dir;
            this.spark(e.x + Math.cos(e.theta) * 10, e.y + Math.sin(e.theta) * 10, tangent + rand(-0.15, 0.15), rand(120, 220), {
              color: pick(fountainColors.concat(this.dark ? '#7cffb2' : '#0f9d8a')), ttl: rand(0.3, 0.6), size: this.dark ? rand(1.2, 2) : rand(1.8, 2.8), gravity: 200, drag: 2.2,
            });
          }
        }
        if (e.life >= e.ttl) this.emitters.splice(i, 1);
      }

      for (let i = this.rockets.length - 1; i >= 0; i--) {
        const r = this.rockets[i];
        if (r.wait > 0) { r.wait -= dt; continue; }
        r.t = Math.min(r.t + dt / r.dur, 1);
        const k = 1 - Math.pow(1 - r.t, 3);
        r.x = r.sx + (r.tx - r.sx) * k;
        r.y = r.sy + (r.ty - r.sy) * k;
        this.spark(r.x, r.y, Math.PI / 2 + rand(-0.5, 0.5), rand(20, 60), {
          color: this.dark ? '#ffe08a' : '#ff8f1f', ttl: rand(0.18, 0.36), size: 1.3, gravity: 60, drag: 3,
        });
        if (r.t >= 1) {
          this.burst(r.x, r.y, { count: r.count, power: r.power, palette: r.palette, crackle: r.crackle });
          this.rockets.splice(i, 1);
        }
      }

      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];
        p.life += dt;
        if (p.life >= p.ttl) {
          if (p.crackle) {
            for (let j = 0; j < 3; j++) {
              this.spark(p.x, p.y, rand(0, Math.PI * 2), rand(30, 80), { color: this.dark ? '#fffbe6' : '#ffb627', ttl: rand(0.1, 0.22), size: 1.1, gravity: 0, drag: 4 });
            }
          }
          this.particles.splice(i, 1);
          continue;
        }
        const drag = Math.exp(-p.drag * dt);
        p.vx *= drag;
        p.vy = p.vy * drag + p.gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
      }

      for (let i = this.flashes.length - 1; i >= 0; i--) {
        const f = this.flashes[i];
        f.life += dt;
        if (f.life >= f.ttl) this.flashes.splice(i, 1);
      }
    }

    draw() {
      const { ctx } = this;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.globalCompositeOperation = this.dark ? 'lighter' : 'source-over';

      for (const f of this.flashes) {
        const t = f.life / f.ttl;
        const radius = f.radius * (0.6 + t * 0.8);
        const gradient = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, radius);
        gradient.addColorStop(0, f.color + (0.45 * (1 - t)) + ')');
        gradient.addColorStop(1, f.color + '0)');
        ctx.globalAlpha = 1;
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(f.x, f.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.lineCap = 'round';
      for (const p of this.particles) {
        const remaining = 1 - p.life / p.ttl;
        let alpha = Math.min(1, remaining * 1.7);
        if (p.flicker) alpha *= 0.55 + Math.random() * 0.45;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = p.color;
        ctx.lineWidth = p.size * (0.55 + remaining * 0.6);
        ctx.beginPath();
        ctx.moveTo(p.x - p.vx * 0.035, p.y - p.vy * 0.035);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();

        // A bright head makes each spark read as glitter against the light page
        if (!this.dark) {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, ctx.lineWidth * 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      for (const r of this.rockets) {
        if (r.wait > 0) continue;
        ctx.globalAlpha = 1;
        ctx.fillStyle = this.dark ? '#fffbe6' : '#ff6a00';
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  /* ------------------------------------------------------------------------
     1. Crackers around Add to cart / Buy now
     ------------------------------------------------------------------------ */
  const TRIGGER_SELECTOR = [
    '[data-product-add-to-cart-button]',
    '#AddToCart',
    '#StickyAddToCart',
    '.product-form__add-button',
    '.product-item__quick-form [type="submit"]',
    '.product-item__cta[type="submit"]',
    '#gokwik-buy-now',
    '.shopify-payment-button__button',
    '.svs-add-to-cart',
    '.svs-buy-now',
    'button[name="add"]',
    '[data-add-to-cart]',
  ].join(',');

  // Catches add-to-cart / buy-now buttons injected by apps (bundles, BOGO, upsells…)
  const TRIGGER_TEXT = /\b(add\s+to\s+(cart|bag)|buy\s+(it\s+)?now|pre[-\s]?order)\b/i;

  let clickEngine = null;

  const getClickEngine = () => {
    if (!clickEngine) {
      const canvas = document.createElement('canvas');
      canvas.className = 'dw-crackers-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      document.body.appendChild(canvas);
      clickEngine = new FireworksEngine(canvas, { fullscreen: true, maxParticles: 1500 });
    }
    return clickEngine;
  };

  const pop = (button) => {
    button.classList.remove('dw-pop');
    void button.offsetWidth; // restart the animation on rapid clicks
    button.classList.add('dw-pop');
    clearTimeout(button._dwPopTimer);
    button._dwPopTimer = setTimeout(() => button.classList.remove('dw-pop'), 950);
  };

  const celebrate = (button, { small = false } = {}) => {
    const rect = button.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    pop(button);
    if (reducedMotion.matches) return;

    const engine = getClickEngine();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const reach = Math.min(rect.width / 2 + 70, 240);

    engine.flash(cx, cy, Math.max(rect.width * 0.7, 90));

    // Chakris spinning at both ends of the button
    engine.chakri(rect.left + 4, cy, { dir: -1, duration: small ? 0.5 : 1, rate: 175 });
    engine.chakri(rect.right - 4, cy, { dir: 1, duration: small ? 0.5 : 1, rate: 175 });

    // Anaar fountains rising from the top edge
    engine.fountain(rect.left + rect.width * 0.25, rect.top, { duration: small ? 0.35 : 0.75, angle: -Math.PI / 2 - 0.3, rate: 190 });
    engine.fountain(rect.left + rect.width * 0.75, rect.top, { duration: small ? 0.35 : 0.75, angle: -Math.PI / 2 + 0.3, rate: 190, delay: 0.08 });
    if (!small && rect.width > 200) {
      engine.fountain(cx, rect.top, { duration: 0.6, spread: 0.3, rate: 150, delay: 0.16 });
    }

    // Phuljhadi glitter thrown off the button outline
    const perimeter = 2 * (rect.width + rect.height);
    const glitter = small ? 22 : 56;
    for (let i = 0; i < glitter; i++) {
      let d = Math.random() * perimeter;
      let x;
      let y;
      if (d < rect.width) { x = rect.left + d; y = rect.top; }
      else if ((d -= rect.width) < rect.height) { x = rect.right; y = rect.top + d; }
      else if ((d -= rect.height) < rect.width) { x = rect.right - d; y = rect.bottom; }
      else { d -= rect.width; x = rect.left; y = rect.bottom - d; }
      const angle = Math.atan2(y - cy, (x - cx) * (rect.height / rect.width) || 0.001);
      engine.spark(x, y, angle + rand(-0.35, 0.35), rand(120, 260), {
        color: pick(['#ffb627', '#ff8f1f', '#e0a83a', '#f72585', '#c8102e', '#7b2cbf']), ttl: rand(0.4, 0.8), size: rand(1.8, 3), gravity: 240, drag: 2.4, flicker: true,
      });
    }

    // Rockets bursting around and above the button
    if (!small) {
      const rockets = rect.width > 260 ? 5 : 4;
      for (let i = 0; i < rockets; i++) {
        const side = i % 2 ? 1 : -1;
        const tx = cx + side * rand(reach * 0.35, reach) * (i === 0 ? 0.15 : 1);
        const ty = Math.max(24, rect.top - rand(60, 170));
        engine.rocket(cx + rand(-rect.width / 4, rect.width / 4), rect.top, tx, ty, {
          delay: i * 0.11, count: rand(64, 86) | 0, power: rand(210, 290),
        });
      }
      // Late low bursts on each side so the button stays framed
      engine.rocket(cx, cy, rect.left - 30, cy - 8, { delay: 0.36, dur: 0.2, count: 48, power: 185 });
      engine.rocket(cx, cy, rect.right + 30, cy - 8, { delay: 0.44, dur: 0.2, count: 48, power: 185 });
    }

    engine.start();
  };

  const findTrigger = (target) => {
    if (!(target instanceof Element)) return null;
    let button = target.closest(TRIGGER_SELECTOR);
    if (!button) {
      const candidate = target.closest('button, a, input[type="submit"], [role="button"]');
      if (candidate && TRIGGER_TEXT.test(candidate.textContent || candidate.value || '')) button = candidate;
    }
    if (!button || button.matches('[is="toggle-button"]')) return null;
    if (button.disabled || button.classList.contains('disabled') || button.getAttribute('aria-disabled') === 'true') return null;
    return button;
  };

  // Capture phase so the burst runs even if an app stops propagation. Never prevents default.
  document.addEventListener('click', (event) => {
    if (!config.enabled || !config.crackers) return;
    const button = findTrigger(event.target);
    if (button) celebrate(button);
  }, true);

  window.DiwaliTheme = Object.assign(window.DiwaliTheme || {}, { celebrate });

  /* ------------------------------------------------------------------------
     2. <diwali-sky> — ambient fireworks behind the hero
     ------------------------------------------------------------------------ */
  class DiwaliSky extends HTMLElement {
    connectedCallback() {
      if (!config.ambient || reducedMotion.matches || this.engine) return;

      this.canvas = document.createElement('canvas');
      this.appendChild(this.canvas);
      this.engine = new FireworksEngine(this.canvas, { dark: true, maxParticles: 520 });
      this.visible = false;

      this.resizeObserver = new ResizeObserver(() => this.engine.resize());
      this.resizeObserver.observe(this);

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
      });
      this.intersectionObserver.observe(this);

      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.engine?.stop();
      this.engine = null;
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.engine || !this.visible || document.hidden) return;
      this.launch();
      this.timer = setTimeout(() => this.schedule(), rand(1000, 1900));
    }

    launch() {
      const { width, height } = this.engine;
      if (!width || !height) return;
      const x = rand(width * 0.08, width * 0.92);
      this.engine.rocket(x, height + 10, x + rand(-50, 50), rand(height * 0.1, height * 0.42), {
        dur: rand(0.9, 1.35), count: rand(60, 84) | 0, power: rand(130, 220), crackle: Math.random() < 0.35,
      });
      this.engine.start();
    }
  }

  /* ------------------------------------------------------------------------
     3. <diwali-countdown data-target="2026-11-08">
     ------------------------------------------------------------------------ */
  class DiwaliCountdown extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.target || '').trim();
      // A bare date is treated as midnight India time
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00+05:30` : raw;
      this.target = Date.parse(iso);
      if (Number.isNaN(this.target)) {
        this.hidden = true;
        return;
      }
      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    update() {
      const remaining = Math.max(0, Math.floor((this.target - Date.now()) / 1000));
      if (remaining === 0) {
        this.setAttribute('ended', '');
        clearInterval(this.timer);
        return;
      }
      const values = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor((remaining % 86400) / 3600),
        minutes: Math.floor((remaining % 3600) / 60),
        seconds: remaining % 60,
      };
      for (const [unit, node] of Object.entries(this.units)) {
        if (node) node.textContent = String(values[unit]).padStart(2, '0');
      }
    }
  }

  /* ------------------------------------------------------------------------
     4. <diwali-coupon data-code="DIWALI"> — tap to copy
     ------------------------------------------------------------------------ */
  class DiwaliCoupon extends HTMLElement {
    connectedCallback() {
      if (this.button) return;
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => this.copy());
    }

    async copy() {
      const code = this.dataset.code || '';
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        field.remove();
      }
      this.setAttribute('copied', '');
      if (config.crackers) celebrate(this.button, { small: true });
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.removeAttribute('copied'), 2000);
    }
  }

  /* ------------------------------------------------------------------------
     5. Product page boosters (snippets/diwali-pdp.liquid): keep the price-derived
        lines in sync with the selected variant
     ------------------------------------------------------------------------ */
  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '{{amount}}';
    const value = cents / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return Math.round(value).toLocaleString('en-IN');
        case 'amount_with_comma_separator':
          return value.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(value).toLocaleString('de-DE');
        default:
          return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    });
  };

  const updateBoosters = (root, variant) => {
    const onSale = variant.compare_at_price > variant.price;

    const save = root.querySelector('[data-dw-save]');
    if (save) {
      save.hidden = !onSale;
      if (onSale) {
        save.querySelector('[data-dw-save-amount]').textContent = formatMoney(variant.compare_at_price - variant.price);
        save.querySelector('[data-dw-save-percent]').textContent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      }
    }

    const bestPrice = root.querySelector('[data-dw-best-price]');
    if (bestPrice) {
      const percent = Number(bestPrice.dataset.percent) || 0;
      bestPrice.querySelector('[data-dw-best-price-amount]').textContent = formatMoney(Math.floor((variant.price * (100 - percent)) / 100));
    }

    const lowStock = root.querySelector('[data-dw-low-stock]');
    if (lowStock) {
      let stock = {};
      try { stock = JSON.parse(root.querySelector('[data-dw-stock]')?.textContent || '{}'); } catch (error) { /* keep empty */ }
      const quantity = stock[variant.id];
      lowStock.hidden = !quantity || !variant.available;
      if (quantity) lowStock.querySelector('[data-dw-low-stock-qty]').textContent = quantity;
    }

    const freeDelivery = root.querySelector('[data-dw-free-delivery]');
    if (freeDelivery) {
      const shipsFree = variant.price >= Number(freeDelivery.dataset.threshold);
      freeDelivery.querySelector('[data-dw-free-yes]').hidden = !shipsFree;
      freeDelivery.querySelector('[data-dw-free-no]').hidden = shipsFree;
    }
  };

  document.addEventListener('variant:changed', (event) => {
    const { variant, formId } = event.detail || {};
    if (!variant || !formId) return;
    document.querySelectorAll(`[data-dw-pdp][data-form-id="${CSS.escape(formId)}"]`).forEach((root) => updateBoosters(root, variant));
  });

  /* ------------------------------------------------------------------------
     6. Shade showcase: selected shade shown as a pill with its colour dot,
        and hovering a swatch previews its name (desktop)
     ------------------------------------------------------------------------ */
  const swatchSelector = '.color-swatch__item';

  const enhanceSwatches = () => {
    if (!config.enabled) return;
    document.querySelectorAll('.product-form__option-selector[data-selector-type="swatch"]').forEach((selector) => {
      const value = selector.querySelector('.product-form__option-value');
      if (!value || value.closest('.dw-shade-pill')) return;

      const pill = document.createElement('span');
      pill.className = 'dw-shade-pill';
      const dot = document.createElement('span');
      dot.className = 'dw-shade-dot';
      dot.setAttribute('aria-hidden', 'true');
      value.replaceWith(pill);
      pill.append(dot, value);

      const shadeCount = selector.querySelectorAll(swatchSelector).length;
      if (shadeCount > 1 && !selector.querySelector('.product-form__option-count')) {
        const count = document.createElement('span');
        count.className = 'product-form__option-count';
        count.textContent = `${shadeCount} shades`;
        pill.after(count);
      }

      const selected = () => selector.querySelector(`.color-swatch__radio:checked + ${swatchSelector}, ${swatchSelector}.is-selected`);
      const show = (swatch) => {
        dot.setAttribute('style', swatch?.getAttribute('style') || '');
        if (swatch) value.textContent = swatch.textContent.trim();
      };
      show(selected());

      selector.addEventListener('change', () => requestAnimationFrame(() => show(selected())));
      selector.addEventListener('pointerover', (event) => {
        const swatch = event.target.closest(swatchSelector);
        if (swatch && event.pointerType === 'mouse') {
          show(swatch);
          pill.classList.add('is-previewing');
        }
      });
      selector.addEventListener('pointerleave', () => {
        show(selected());
        pill.classList.remove('is-previewing');
      });
    });
  };

  /* ------------------------------------------------------------------------
     7. Collapse app sections that render nothing (e.g. the widget of a disabled
        app), which otherwise leave a large empty gap. Re-expands if content loads.
     ------------------------------------------------------------------------ */
  const watchAppSections = () => {
    document.querySelectorAll('.shopify-section--apps > .vertical-breather:not([data-dw-watched])').forEach((inner) => {
      inner.setAttribute('data-dw-watched', '');
      const update = () => {
        const hasContent = [...inner.children].some((child) => child.getBoundingClientRect().height > 2);
        inner.style.paddingBlock = hasContent ? '' : '0px';
      };
      const observer = new ResizeObserver(update);
      [...inner.children].forEach((child) => observer.observe(child));
      update();
    });
  };

  /* ------------------------------------------------------------------------
     8. Keep the WhatsApp app's floating button clear of the bottom app bar and
        the sticky add-to-cart bar. Its button lives in an open shadow root, so a
        style is injected there; the offset comes from --dw-float-offset (diwali.css).
     ------------------------------------------------------------------------ */
  const liftFloatingWidgets = () => {
    const root = document.querySelector('gty-whatsapp-chat-button')?.shadowRoot;
    if (!root || root.querySelector('style[data-dw-lift]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-dw-lift', '');
    style.textContent = '.gty-wacb-wrapper { bottom: calc(16px + var(--dw-float-offset, 0px)) !important; transition: bottom .2s ease; }';
    root.appendChild(style);
  };

  /* ------------------------------------------------------------------------
     10. GoKwik "Buy now" must buy the product of the form it sits in.
         GoKwik serialises document.querySelector(window.productFormSelector) at click time, and the
         theme sets that to '.product__info'. Inside a quick view that matches nothing on collection pages
         (GoKwik crashes and falls back to the default checkout) and matches the Featured product on the
         homepage (wrong product). Point it at the clicked button's own form before GoKwik's onclick runs.
     ------------------------------------------------------------------------ */
  const defaultProductFormSelector = window.productFormSelector || '.product__info';

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element && event.target.closest('#gokwik-buy-now');
    if (!button) return;
    const quickView = button.closest('quick-buy-popover[id], quick-buy-drawer[id]');
    window.productFormSelector = quickView ? `#${CSS.escape(quickView.id)} .product-form` : defaultProductFormSelector;
  }, true);

  // Focal swaps in freshly rendered blocks on variant change; re-apply enhancements when it does.
  let pending = 0;
  const refresh = () => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => {
      enhanceSwatches();
      watchAppSections();
      liftFloatingWidgets();
    });
  };
  refresh();
  new MutationObserver((records) => {
    // Countdown ticks rewrite their digits every second; those never need a refresh
    if (records.some((record) => !record.target.closest?.('diwali-countdown'))) refresh();
  }).observe(document.body, { childList: true, subtree: true });

  /* ------------------------------------------------------------------------
     9. <diwali-compare> — before / after slider (sections/diwali-before-after.liquid)
        Mouse: press or drag anywhere. Touch: horizontal drag moves the divider while
        vertical swipes still scroll the page (touch-action: pan-y). Keyboard: the
        visually hidden range input. Sweeps once on first view to show it moves.
     ------------------------------------------------------------------------ */
  class DiwaliCompare extends HTMLElement {
    connectedCallback() {
      if (this.range) return;
      this.range = this.querySelector('input[type="range"]');

      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.stopIntro();
        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.dragging = event.pointerType === 'mouse';
        if (this.dragging) this.startDrag(event);
      });

      this.addEventListener('pointermove', (event) => {
        if (event.pointerId !== this.pointerId) return;
        if (!this.dragging && Math.abs(event.clientX - this.startX) > 6) {
          this.dragging = true;
          this.startDrag(event);
        }
        if (this.dragging) this.setFromX(event.clientX);
      });

      const end = () => {
        this.dragging = false;
        this.pointerId = null;
        this.classList.remove('is-dragging');
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      this.range?.addEventListener('input', () => {
        this.stopIntro();
        this.set(Number(this.range.value));
      });

      if (!reducedMotion.matches) {
        this.observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            this.observer.disconnect();
            this.playIntro();
          }
        }, { threshold: 0.5 });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.stopIntro();
    }

    startDrag(event) {
      this.classList.add('is-dragging');
      try { this.setPointerCapture(event.pointerId); } catch (error) { /* pointer already released */ }
      this.setFromX(event.clientX);
    }

    setFromX(clientX) {
      const rect = this.getBoundingClientRect();
      this.set(((clientX - rect.left) / rect.width) * 100);
    }

    set(percent) {
      const value = Math.min(100, Math.max(0, percent));
      this.style.setProperty('--dw-pos', `${value}%`);
      if (this.range) this.range.value = Math.round(value);
    }

    playIntro() {
      const keyframes = [[0, 50], [0.35, 78], [0.75, 24], [1, 50]];
      const duration = 2200;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min((now - start) / duration, 1);
        let i = 0;
        while (i < keyframes.length - 2 && t > keyframes[i + 1][0]) i++;
        const [t0, v0] = keyframes[i];
        const [t1, v1] = keyframes[i + 1];
        const local = (t - t0) / (t1 - t0);
        const eased = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
        this.set(v0 + (v1 - v0) * eased);
        if (t < 1) this.introFrame = requestAnimationFrame(step);
      };
      this.introFrame = requestAnimationFrame(step);
    }

    stopIntro() {
      cancelAnimationFrame(this.introFrame);
    }
  }

  if (!customElements.get('diwali-compare')) customElements.define('diwali-compare', DiwaliCompare);
  if (!customElements.get('diwali-sky')) customElements.define('diwali-sky', DiwaliSky);
  if (!customElements.get('diwali-countdown')) customElements.define('diwali-countdown', DiwaliCountdown);
  if (!customElements.get('diwali-coupon')) customElements.define('diwali-coupon', DiwaliCoupon);
})();
